<script setup lang="ts">
import CurrencyText from './CurrencyText.vue'
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppImage from '@uiKit/elements/Image.vue'
import AppButton from '@uiKit/elements/Button.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import emptyStateIcon from '@assets/images/empty-state-icon.png'

interface Props {
  title: string
  description?: string
  /** PiIcon name — auto-rendered inside a subtle pastel-violet orb. Use the
   *  `#icon` slot for full custom overrides. */
  icon?: PiIconName
  size?: 'default' | 'compact'
  primaryActionLabel?: string
  primaryActionIcon?: string
  secondaryActionLabel?: string
  secondaryActionIcon?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'default',
})

defineEmits<{
  (e: 'primary-action'): void
  (e: 'secondary-action'): void
}>()

const isCompact = computed(() => props.size === 'compact')
const titleType = computed(() => (isCompact.value ? 'heading' : 'title-lg'))
const descriptionType = computed(() => (isCompact.value ? 'body-sm' : 'heading'))
const iconWidth = computed(() => (isCompact.value ? '96px' : '190px'))
// Rhythm — generous outer breath (icon ↔ text-cluster ↔ actions) so the
// cluster reads as three distinct beats; tight inner breath inside the
// text cluster so title and description belong together.
const stackGap = computed(() => (isCompact.value ? '4' : '6'))
</script>

<template>
  <Box
    direction="col"
    align="center"
    justify="center"
    :gap="stackGap"
    :class="['empty-state', `empty-state--${size}`]"
  >
    <slot name="icon">
      <Box v-if="icon" align="center" justify="center" class="empty-state__icon-orb">
        <PiIcon :name="icon" />
      </Box>
      <AppImage v-else :src="emptyStateIcon" alt="" :width="iconWidth" />
    </slot>

    <Box direction="col" align="center" gap="2" class="empty-state__text">
      <Text :type="titleType" weight="bold" align="center">{{ title }}</Text>
      <Text
        v-if="description"
        :type="descriptionType"
        weight="regular"
        color="muted"
        align="center"
        class="empty-state__description"
      >
        <CurrencyText :text="description" />
      </Text>
    </Box>

    <Box
      v-if="primaryActionLabel || secondaryActionLabel"
      align="center"
      gap="2"
      class="empty-state__actions"
    >
      <AppButton
        v-if="secondaryActionLabel"
        :label="secondaryActionLabel"
        :icon="secondaryActionIcon"
        :icon-pos="secondaryActionIcon ? 'right' : undefined"
        variant="outline"
        @click="$emit('secondary-action')"
      />
      <AppButton
        v-if="primaryActionLabel"
        :label="primaryActionLabel"
        :icon="primaryActionIcon"
        :icon-pos="primaryActionIcon ? 'right' : undefined"
        @click="$emit('primary-action')"
      />
    </Box>
  </Box>
</template>

<style scoped>
/* EmptyState always fills the remaining space of its parent so the page
   reads as "this whole area is empty" rather than as a small widget at
   the top. Consumers must ensure the parent is a flex column with
   available height (display: flex; flex-direction: column + flex: 1). */
.empty-state {
  flex: 1;
  align-self: stretch;
  width: 100%;
  min-height: 240px;
}

.empty-state--compact {
  padding: 1.5rem 1rem;
}

.empty-state__description {
  max-width: 360px;
}

.empty-state--compact .empty-state__description {
  max-width: 280px;
}

/* Subtle pastel-violet orb rendered when the `icon` prop is set. The kit's
   `.pi { font-size: var(--p-icon-size) }` rule beats inherited font-size on
   specificity, so we override the var locally instead of fighting :deep. */
.empty-state__icon-orb {
  --p-icon-size: 40px;
  width: 96px;
  height: 96px;
  border-radius: 999px;
  background: var(--p-primary-50);
  color: var(--p-primary-600);
  line-height: 1;
}

.empty-state--compact .empty-state__icon-orb {
  --p-icon-size: 28px;
  width: 72px;
  height: 72px;
}
</style>
